import { z } from "zod";
import { Alert, Button, Group, Modal, PasswordInput, TextInput } from "@mantine/core";
import { IconAlertTriangle, IconInfoCircle } from "@tabler/icons-react";
import { useCallback, useMemo, type FC } from "react";
import { useTRPC } from "../../../trpc";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useNavigate } from "@tanstack/react-router";
import { useBasePath } from "../../../hooks/router";
import { emailMismatch, emailSchema, passwordSchemaLength } from "../../../utils/schema";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "@tanstack/react-query";
import type { UserDetails } from "@project/api";
import { useMapkaNotification } from "../../../hooks/notifications.js";

interface ChangeEmailModalProps {
  open: boolean;
  onClose: () => void;
  user: UserDetails;
}

interface FormValues {
  [key: string]: string;
  newEmail: string;
  confirmEmail: string;
  password: string;
}

export const ChangeEmailModal: FC<ChangeEmailModalProps> = ({ open, onClose, user: { email } }) => {
  const intl = useIntl();
  const trpc = useTRPC();
  const navigate = useNavigate();
  const pathname = useBasePath();
  const mapkaNotification = useMapkaNotification();

  const { mutate: changeEmail, isPending: isPendingChangeEmail, error } = useMutation(trpc.changeEmail.mutationOptions());

  const handleChangeEmail = useCallback((input: FormValues) => {
    changeEmail(input, {
      onSuccess: () => {
        mapkaNotification({
          variant: "success",
          title: intl.formatMessage({
            defaultMessage: "Email changed",
            id: "notification.emailChanged",
          }),
          message: intl.formatMessage({
            defaultMessage: "Request verification email has been sent to your new email address",
            id: "notification.emailChangedMessage",
          }),
        });
        void navigate({ to: `${pathname}/email-verification` });
      },
    });
  }, [changeEmail, intl, navigate]);

  const validate = useMemo(() => zod4Resolver(
    z
      .object({
        newEmail: emailSchema(intl),
        confirmEmail: emailSchema(intl),
        password: passwordSchemaLength(intl),
      }).refine(data => data.newEmail === data.confirmEmail, {
        message: emailMismatch(intl),
        path: ["confirmEmail"],
      }),
  ), [intl]);

  const { onSubmit, getInputProps } = useForm({
    validateInputOnBlur: true,
    initialValues: {
      newEmail: email,
      confirmEmail: email,
      password: "",
    },
    validate,
  });

  const errorMessage = useMemo(() => {
    if (error?.message === "User or password are incorrect") {
      return intl.formatMessage({
        defaultMessage: "User or password are incorrect",
        id: "error.userOrPasswordIncorrect",
      });
    }
    else if (error?.message === "Email already in use") {
      return intl.formatMessage({
        defaultMessage: "Email is already in use",
        id: "error.emailInUse",
      });
    }
    return error?.message;
  }, [error, intl]);

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={intl.formatMessage({
        defaultMessage: "Change email address",
        id: "title.changeEmailAddress",
      })}
    >
      {errorMessage
        && (
          <Alert variant="light" color="red" icon={<IconAlertTriangle />}>
            {errorMessage}
          </Alert>
        )}

      <Alert variant="light" icon={<IconInfoCircle />}>
        <FormattedMessage
          defaultMessage="Once email address is changed, user will be redirected to email verification flow."
          id="text.emailChangeDescription"
        />
      </Alert>

      <form onSubmit={onSubmit(handleChangeEmail)}>
        <TextInput
          label={intl.formatMessage({
            defaultMessage: "New email",
            id: "label.newEmail",
          })}
          autoComplete="email"
          {...getInputProps("newEmail")}
        />
        <TextInput
          label={intl.formatMessage({
            defaultMessage: "Confirm email",
            id: "label.confirmEmail",
          })}
          autoComplete="email"
          {...getInputProps("confirmEmail")}
        />
        <PasswordInput
          label={intl.formatMessage({
            defaultMessage: "Password",
            id: "label.password",
          })}
          autoComplete="password"
          {...getInputProps("password")}
        />

        <Group gap="xs" justify="flex-end" pt="lg">
          <Button variant="secondary" type="reset" loading={isPendingChangeEmail} onClick={onClose}>
            <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
          </Button>

          <Button variant="tertiary" type="submit" loading={isPendingChangeEmail}>
            <FormattedMessage id="button.save" defaultMessage="Save" />
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
